/**
 * CommandCentreGuidedTour — Feb 2026
 * ===================================
 *
 * 60-second guided walk-through of every product on the Admin Analysis
 * home page. Perfect for a new admin's first day — one click, hands-free
 * auto-scroll, and by the end they've seen every tile with a plain-
 * English description of what it does.
 *
 * How it works
 * ------------
 *  • Reads `CC_PRODUCT_DESCRIPTIONS` (the central copy map) and walks
 *    the tiles in the order they appear in the DOM.
 *  • For each step: smooth-scrolls to the tile, drops a purple highlight
 *    ring around it, and pins a floating "Now viewing" card at the
 *    bottom of the viewport with the title, description, and controls.
 *  • Auto-advance every 6 s; user can Prev / Next / Pause / Close.
 *  • No dependency on hover state — the tour renders its own copy card
 *    from the descriptions map so the CSS-only `group-hover` in
 *    `InfoTooltipWrapper` doesn't need to be simulated.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Play, Pause, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { CC_PRODUCT_DESCRIPTIONS } from './ccProductDescriptions';

const STEP_MS = 6000;
const SCROLL_MARGIN_PX = 120; // clear the sticky header + sub-nav
const HIGHLIGHT_PADDING_PX = 8;

const CommandCentreGuidedTour = ({ open, onClose }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [rect, setRect] = useState(null);
  const timerRef = useRef(null);

  // Build the ordered step list from the tiles ACTUALLY present in DOM.
  // We snapshot at open-time so late-mounting tiles don't shuffle the
  // sequence mid-tour. Falls back to the copy-map order if DOM order
  // can't be established.
  const steps = useMemo(() => {
    if (!open) return [];
    const keys = Object.keys(CC_PRODUCT_DESCRIPTIONS);
    const withNodes = keys
      .map((key) => {
        const el = document.querySelector(`[data-testid="info-wrap-${key}"]`);
        return el ? { key, el, top: el.getBoundingClientRect().top + window.scrollY } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.top - b.top);
    return withNodes;
  }, [open]);

  const currentStep = steps[index];

  // Scroll + highlight when the step changes. Recompute the highlight
  // rect on window resize so the ring stays glued to the tile.
  useEffect(() => {
    if (!open || !currentStep) return undefined;
    const { el } = currentStep;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Give the smooth-scroll a moment to settle before measuring.
    const settle = setTimeout(() => {
      const r = el.getBoundingClientRect();
      setRect({
        top: r.top + window.scrollY - HIGHLIGHT_PADDING_PX,
        left: r.left + window.scrollX - HIGHLIGHT_PADDING_PX,
        width: r.width + HIGHLIGHT_PADDING_PX * 2,
        height: r.height + HIGHLIGHT_PADDING_PX * 2,
      });
    }, 450);
    const onResize = () => {
      const r = el.getBoundingClientRect();
      setRect({
        top: r.top + window.scrollY - HIGHLIGHT_PADDING_PX,
        left: r.left + window.scrollX - HIGHLIGHT_PADDING_PX,
        width: r.width + HIGHLIGHT_PADDING_PX * 2,
        height: r.height + HIGHLIGHT_PADDING_PX * 2,
      });
    };
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(settle);
      window.removeEventListener('resize', onResize);
    };
  }, [open, currentStep]);

  // Auto-advance timer. Cleared while paused or when the user drives
  // manually via Prev / Next.
  useEffect(() => {
    if (!open || paused || !steps.length) return undefined;
    timerRef.current = setTimeout(() => {
      setIndex((i) => (i + 1 < steps.length ? i + 1 : i));
    }, STEP_MS);
    return () => clearTimeout(timerRef.current);
  }, [open, paused, index, steps.length]);

  // Reset when reopened
  useEffect(() => {
    if (open) {
      setIndex(0);
      setPaused(false);
    }
  }, [open]);

  const next = useCallback(() => {
    setIndex((i) => Math.min(i + 1, steps.length - 1));
  }, [steps.length]);
  const prev = useCallback(() => {
    setIndex((i) => Math.max(i - 1, 0));
  }, []);

  // Keyboard controls: Esc closes, ← / → step, space toggles pause.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (ev) => {
      if (ev.key === 'Escape') { ev.preventDefault(); onClose(); }
      else if (ev.key === 'ArrowRight') { ev.preventDefault(); next(); }
      else if (ev.key === 'ArrowLeft') { ev.preventDefault(); prev(); }
      else if (ev.key === ' ') { ev.preventDefault(); setPaused((p) => !p); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, next, prev, onClose]);

  if (!open) return null;
  if (!steps.length) {
    // Nothing to tour — probably rendered before the tiles mounted.
    // Show a small toast-style card that closes on click.
    return (
      <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40" onClick={onClose} data-testid="cc-tour-empty">
        <div className="bg-white rounded-lg p-6 max-w-sm text-center">
          <p className="text-sm text-gray-700">
            Loading Command Centre tiles… Give the page a moment then start the tour again.
          </p>
        </div>
      </div>
    );
  }

  const meta = CC_PRODUCT_DESCRIPTIONS[currentStep.key] || {};

  return (
    <>
      {/* Scrim — dim the rest of the page but keep it clickable for
          Escape / Close. Does NOT block the highlighted tile. */}
      <div
        className="fixed inset-0 z-[9990] bg-black/45 pointer-events-none"
        data-testid="cc-tour-scrim"
      />

      {/* Highlight ring around the current tile. Positioned absolutely
          in document coordinates so it moves with the page as the tile
          scrolls. */}
      {rect && (
        <div
          className="absolute z-[9991] pointer-events-none rounded-xl"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            boxShadow: '0 0 0 3px #7B4FD8, 0 0 0 9999px rgba(0,0,0,0.45)',
            transition: 'top 0.35s ease, left 0.35s ease, width 0.35s ease, height 0.35s ease',
            borderRadius: 12,
          }}
          data-testid="cc-tour-highlight"
        />
      )}

      {/* Floating control + description card, pinned near the bottom
          of the viewport so it never overlaps the highlighted tile. */}
      <div
        className="fixed z-[9999] left-1/2 -translate-x-1/2 bottom-6 w-[min(720px,calc(100vw-24px))]"
        data-testid="cc-tour-card"
      >
        <div className="bg-white rounded-xl shadow-2xl overflow-hidden" style={{ border: '1px solid #E0E0E0' }}>
          {/* Header bar */}
          <div className="flex items-center gap-2 px-4 py-2.5" style={{ backgroundColor: '#7B4FD8', color: 'white' }}>
            <MapPin size={14} />
            <span className="text-xs font-semibold uppercase tracking-wider">
              Guided Tour — {index + 1} of {steps.length}
            </span>
            <div className="ml-auto flex items-center gap-1">
              <button
                type="button"
                onClick={prev}
                disabled={index === 0}
                className="p-1.5 rounded hover:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Previous (←)"
                data-testid="cc-tour-prev"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                onClick={() => setPaused((p) => !p)}
                className="p-1.5 rounded hover:bg-white/15"
                title={paused ? 'Play (Space)' : 'Pause (Space)'}
                data-testid="cc-tour-play-pause"
              >
                {paused ? <Play size={16} /> : <Pause size={16} />}
              </button>
              <button
                type="button"
                onClick={next}
                disabled={index === steps.length - 1}
                className="p-1.5 rounded hover:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Next (→)"
                data-testid="cc-tour-next"
              >
                <ChevronRight size={16} />
              </button>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded hover:bg-white/15 ml-1"
                title="Close (Esc)"
                data-testid="cc-tour-close"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Body — title + description */}
          <div className="p-4">
            <h3 className="text-base font-bold mb-1" style={{ color: '#1A1209' }}>{meta.title || 'Command Centre'}</h3>
            <p className="text-sm leading-snug whitespace-pre-line" style={{ color: '#4B5563' }}>
              {meta.description}
            </p>
            {/* Progress bar */}
            <div className="mt-3 h-1 w-full rounded-full" style={{ backgroundColor: '#F3F4F6' }}>
              <div
                className="h-1 rounded-full transition-all duration-500"
                style={{
                  width: `${Math.round(((index + 1) / steps.length) * 100)}%`,
                  backgroundColor: '#7B4FD8',
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CommandCentreGuidedTour;
